import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent } from '../ui/dialog';
import { API_URLS } from '../../config/api';
import MediaDisplay from '../MediaDisplay';
import ProjectQuestionForm from '../ProjectQuestionForm';
import {
  Briefcase,
  Search,
  Filter,
  Calendar,
  Clock,
  Star,
  Users,
  CheckCircle,
  AlertCircle,
  Play,
  Pause,
  Eye,
  MessageSquare,
  TrendingUp,
  Award,
  Target,
  Zap,
  Globe,
  Mail,
  MessageCircle,
  Shield,
  ArrowRight,
  Plus,
  Share2,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon
} from 'lucide-react';

// Hover scroll effect styles
const customStyles = `
  .group:hover .group-hover\\:object-bottom {
    transition: all 20s ease;
  }
  .image-short { transition: all 3s ease !important; }
  .image-medium { transition: all 8s ease !important; }
  .image-long { transition: all 15s ease !important; }
  .image-very-long { transition: all 25s ease !important; }
`;

function getImageTransitionClass(naturalHeight) {
  if (!naturalHeight) return '';
  if (naturalHeight < 800) return 'image-short';
  if (naturalHeight < 1500) return 'image-medium';
  if (naturalHeight < 2500) return 'image-long';
  return 'image-very-long';
}

const ProjectsPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 10;
  const [imageErrorIds, setImageErrorIds] = useState(() => new Set());
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // URL'den ?kategori= ile gelen kategoriyi uygula (projeler-kategoriler sayfasından gelince)
  useEffect(() => {
    const kategori = searchParams.get('kategori');
    if (kategori && kategori !== 'all') {
      setSelectedCategory(kategori);
    }
  }, [searchParams]);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data?.whatsapp) {
          setWhatsappNumber(json.data.whatsapp);
        }
      }
    } catch (e) {
      console.error('WhatsApp numarası alınamadı:', e);
    }
  };

  const handleShareProject = async (project) => {
    const shareData = {
      title: project.name,
      text: project.description,
      url: `${window.location.origin}/project/${project.id}`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(shareData.url);
        alert('Proje linki kopyalandı!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch both projects and categories
      const [projectsResponse, categoriesResponse] = await Promise.all([
        fetch(API_URLS.PROJECTS),
        fetch(API_URLS.PROJECT_CATEGORIES)
      ]);
      
      const projectsData = await projectsResponse.json();
      const categoriesData = await categoriesResponse.json();
      
      if (projectsData.status === 'error') {
        setError(projectsData.message || 'Projeler yüklenirken hata oluştu');
        return;
      }
      
      setProjects(projectsData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Dinamik status'lar projelerden hesaplanacak
  const getProjectStatuses = () => {
    const statusCounts = projects.reduce((acc, project) => {
      const status = project.status || 'completed';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const statusConfig = {
      'completed': { name: 'Tamamlandı', color: 'text-green-600' },
      'in_progress': { name: 'Devam Ediyor', color: 'text-blue-600' },
      'planned': { name: 'Planlandı', color: 'text-yellow-600' },
      'on_hold': { name: 'Beklemede', color: 'text-orange-600' }
    };

    const projectStatuses = [
      { id: 'all', name: 'Tümü', count: projects.length, color: 'text-gray-600' }
    ];

    Object.entries(statusCounts).forEach(([status, count]) => {
      if (statusConfig[status]) {
        projectStatuses.push({
          id: status,
          name: statusConfig[status].name,
          count,
          color: statusConfig[status].color
        });
      }
    });

    return projectStatuses;
  };


  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700',
      in_progress: 'bg-blue-100 text-blue-700',
      planned: 'bg-yellow-100 text-yellow-700',
      on_hold: 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: CheckCircle,
      in_progress: Play,
      planned: Clock,
      on_hold: Pause
    };
    return icons[status] || CheckCircle;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'border-l-red-500',
      medium: 'border-l-yellow-500',
      low: 'border-l-green-500'
    };
    return colors[priority] || 'border-l-gray-500';
  };

  const filteredProjects = projects.filter(project => {
    // Arama terimini küçük harfe çevir
    const searchLower = searchQuery.toLowerCase().trim();
    
    // Eğer arama terimi boşsa, tüm projeleri göster
    if (!searchLower) {
      const matchesCategory = selectedCategory === 'all' || project.category_id === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || project.status === selectedStatus;
      return matchesCategory && matchesStatus;
    }
    
    // Arama terimini kelimelere böl
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);
    
    // Her kelime için arama yap
    const matchesSearch = searchWords.every(word => {
      // Proje adında ara
      const nameMatch = project.name?.toLowerCase().includes(word) || false;
      
      // Açıklamada ara
      const descMatch = project.description?.toLowerCase().includes(word) || false;
      
      // Detaylı açıklamada ara
      const detailedDescMatch = project.detailed_description?.toLowerCase().includes(word) || false;
      
      // Özelliklerde ara
      const featuresMatch = Array.isArray(project.features) 
        ? project.features.some(feature => 
            (typeof feature === 'string' ? feature : feature.name || feature.title || '').toLowerCase().includes(word)
          )
        : false;
      
      // Teknolojilerde ara
      const techMatch = Array.isArray(project.technologies)
        ? project.technologies.some(tech => 
            (typeof tech === 'string' ? tech : tech.name || '').toLowerCase().includes(word)
          )
        : false;
      
      // Müşteri adında ara
      const clientMatch = project.client_name?.toLowerCase().includes(word) || false;
      
      // Kategori adında ara
      const categoryMatch = categories.find(c => c.id === project.category_id)?.name?.toLowerCase().includes(word) || false;
      
      return nameMatch || descMatch || detailedDescMatch || featuresMatch || techMatch || clientMatch || categoryMatch;
    });
    
    const matchesCategory = selectedCategory === 'all' || project.category_id === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || project.status === selectedStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
  const startIndex = (currentPage - 1) * projectsPerPage;
  const endIndex = startIndex + projectsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedStatus, searchQuery]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Hata Oluştu</h3>
          <p className="text-gray-600 mb-8">{error}</p>
          <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700">
            Tekrar Dene
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white py-20 relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-3 bg-white/20 backdrop-blur-lg px-6 py-3 rounded-full text-lg font-medium">
                <Briefcase className="w-6 h-6" />
                <span>Portfolyo</span>
                <Badge className="bg-blue-400 text-blue-900">Showcase</Badge>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                <span className="inline-block">
                  Projelerimiz
                </span>
                <br />
                <span className="inline-block">
                  Portfolyo
                </span>
                <br />
                <span className="text-3xl md:text-4xl text-white font-normal">
                  Başarı • Kalite • İnovasyon
                </span>
              </h1>
              
              <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
                Gerçekleştirdiğimiz projeleri keşfedin. Her proje, müşteri memnuniyeti ve 
                teknolojik mükemmellik için verdiğimiz taahhüdün kanıtıdır.
              </p>

              {/* Hero: Proje Talebi + WhatsApp + Tüm kategoriler */}
              <div className="flex flex-wrap justify-center gap-4 pt-6">
                <Button asChild className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg">
                  <Link to="/iletisim">
                    <Plus className="w-5 h-5 mr-2" />
                    Proje Talebi
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-white bg-white/10 text-white hover:bg-white/20 backdrop-blur"
                >
                  <a
                    href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent('Merhaba, proje hizmetleriniz hakkında bilgi almak istiyorum.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp iletişim
                  </a>
                </Button>
                <Button asChild variant="ghost" className="text-white hover:bg-white/20">
                  <Link to="/projeler-kategoriler">
                    Tüm kategoriler
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        {/* Header Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold text-gray-900">Proje Portfolyosu</h2>
            <p className="text-gray-600">Gerçekleştirdiğimiz projeleri keşfedin</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button asChild className="bg-blue-600 hover:bg-blue-700">
              <Link to="/iletisim">
                <Plus className="w-5 h-5 mr-2" />
                Proje Talebi
              </Link>
            </Button>
          </div>
        </div>

        {/* Mobile Filters - Wrap Layout */}
        <div className="lg:hidden space-y-4 mb-6">
          {/* Mobile Categories */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Kategoriler
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span>Tümü</span>
                <Badge variant="secondary" className={selectedCategory === 'all' ? 'bg-white text-blue-600' : 'bg-white'}>
                  {projects.length}
                </Badge>
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{category.name}</span>
                  <Badge variant="secondary" className={selectedCategory === category.id ? 'bg-white text-blue-600' : 'bg-white'}>
                    {projects.filter(p => p.category_id === category.id).length}
                  </Badge>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Status */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Durum
            </h3>
            <div className="flex flex-wrap gap-2">
              {getProjectStatuses().map((status) => (
                <button
                  key={status.id}
                  onClick={() => setSelectedStatus(status.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedStatus === status.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{status.name}</span>
                  <Badge variant="secondary" className={selectedStatus === status.id ? 'bg-white text-blue-600' : 'bg-white'}>
                    {status.count}
                  </Badge>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar + Projects Layout */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters - Desktop Only */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0 space-y-6">
            {/* Search */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Search className="w-5 h-5 mr-2 text-blue-600" />
                  Arama
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Proje ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Category Filter */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Filter className="w-5 h-5 mr-2 text-blue-600" />
                  Kategoriler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="font-medium">Tümü</span>
                    <Badge variant="secondary" className="text-xs">
                      {projects.length}
                    </Badge>
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all ${
                        selectedCategory === category.id
                          ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="font-medium">{category.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {projects.filter(p => p.category_id === category.id).length}
                      </Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Status Filter */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Durum
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {getProjectStatuses().map((status) => (
                    <button
                      key={status.id}
                      onClick={() => setSelectedStatus(status.id)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all ${
                        selectedStatus === status.id
                          ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="font-medium">{status.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {status.count}
                      </Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Projects List */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-20">
                <div className="rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Projeler yükleniyor...</p>
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="space-y-6">
                {paginatedProjects.map((project, index) => (
            <Card
              key={project.id}
              className={`group border-0 shadow-lg overflow-hidden ${getPriorityColor(project.priority)}`}
              style={{ height: '358.75px' }}
            >
              <div className="flex flex-col md:flex-row h-full">
                {/* Left Side - Project Image */}
                <div className={`w-full md:w-64 md:flex-shrink-0 border-l-4 ${getPriorityColor(project.priority)}`}>
                  <div className="aspect-video md:h-full bg-gray-100 relative overflow-hidden md:[aspect-ratio:auto]">
                    {(!project.image_url || !project.image_url.trim() || imageErrorIds.has(project.id)) ? (
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-white/50" strokeWidth={1.5} />
                      </div>
                    ) : (
                      <MediaDisplay
                        src={project.image_url}
                        alt={project.name}
                        className="w-full h-full object-cover object-top transition-all duration-[20s] ease group-hover:object-bottom"
                        videoProps={{ controls: true }}
                        imgProps={{
                          loading: "lazy",
                          onError: () => setImageErrorIds(prev => new Set(prev).add(project.id)),
                          onLoad: (e) => {
                            const nh = e.target.naturalHeight;
                            const cls = getImageTransitionClass(nh);
                            if (cls) e.target.classList.add(cls);
                          }
                        }}
                      />
                    )}
                    {/* Status Badge on Image */}
                    <div className="absolute top-3 left-3 z-10">
                      <Badge className={getStatusColor(project.status)}>
                        {React.createElement(getStatusIcon(project.status), { className: "w-3 h-3 mr-1" })}
                        {getProjectStatuses().find(s => s.id === project.status)?.name || 'Tamamlandı'}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Right Side - Project Content */}
                <div className="flex-1 min-w-0 flex flex-col">
                  <CardHeader className="pb-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <Badge variant="secondary">
                          {categories.find(c => c.id === project.category_id)?.name || 'Proje'}
                        </Badge>
                      </div>
                      
                      <CardTitle className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors cursor-pointer">
                        <Link to={`/project/${project.id}`}>
                          {project.name}
                        </Link>
                      </CardTitle>
                      
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                {/* Project Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span>{project.duration || '6 hafta'}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span>{project.rating || '4.8'} puan</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Users className="w-4 h-4 text-orange-500" />
                    <span>{project.reviews_count || 0} değerlendirme</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700">Özellikler</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.features && project.features.length > 0 ? (
                      project.features.slice(0, 3).map((feature, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Web Tasarım
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="text-gray-700 hover:text-white hover:bg-purple-600"
                    >
                      <Link to={`/project/${project.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        Projeyi İncele
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-700 hover:text-white hover:bg-purple-600"
                      onClick={() => handleShareProject(project)}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Paylaş
                    </Button>
                  </div>
                  
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => {
                      setSelectedProject(project);
                      setShowQuestionDialog(true);
                    }}
                  >
                    Proje Talebi
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                  </CardContent>
                </div>
              </div>
            </Card>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Önceki
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                              className={currentPage === page ? "bg-blue-600 hover:bg-blue-700" : ""}
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="px-2 text-gray-500">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1"
                    >
                      Sonraki
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {/* Page Info */}
                {totalPages > 1 && (
                  <div className="text-center text-sm text-gray-600 pt-2">
                    Sayfa {currentPage} / {totalPages} (Toplam {filteredProjects.length} proje)
                  </div>
                )}
              </div>
            ) : (
              /* Empty State */
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Briefcase className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Proje Bulunamadı</h3>
                <p className="text-gray-600 mb-8">Arama kriterlerinize uygun proje bulunamadı.</p>
                <Button asChild className="bg-blue-600 hover:bg-blue-700">
                  <Link to="/iletisim">
                    <Plus className="w-5 h-5 mr-2" />
                    Proje Talebi Oluşturun
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Contact Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">Proje İhtiyacınız İçin Bize Ulaşın</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Hayalinizdeki projeyi gerçeğe dönüştürmek için ekibimizle iletişime geçin. Size özel çözümler ve ücretsiz danışmanlık için buradayız.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/iletisim">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg">
                  <Mail className="w-5 h-5 mr-2" />
                  İletişime Geç
                </Button>
              </Link>
              <a
                href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent('Merhaba, proje hizmetleriniz hakkında bilgi almak istiyorum.')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-green-600 hover:bg-green-700 px-8 py-4 text-lg border-2 border-white">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp İletişim
                </Button>
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Clock className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">7/24 Destek</h3>
                <p className="text-blue-100">Proje geliştirme konusunda kesintisiz destek</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Shield className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Güvenli İşbirliği</h3>
                <p className="text-blue-100">Profesyonel ve güvenilir proje yönetimi</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Award className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Uzman Ekip</h3>
                <p className="text-blue-100">Proje geliştirme konusunda deneyimli ekibimiz</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-w-2xl p-0 border-0 shadow-xl overflow-hidden" hideClose>
          {selectedProject && (
            <ProjectQuestionForm
              project={selectedProject}
              onClose={() => setShowQuestionDialog(false)}
              formTitle="Proje Hakkında Soru Sor"
              projectLabel="İlgili proje"
              showQuestion={true}
            />
          )}
        </DialogContent>
      </Dialog>

      </div>
    </>
  );
};

export default ProjectsPage;